import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Chip, Collapse, IconButton, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import moment from 'moment';
import deepmerge from 'deepmerge';

import useNotification from '../../../../../../core/hooks/use_notification';
import { getCartEventsNotificationData, getCustomerActivitiesNotificationData, getPodsNotificationData } from '../../../../../../core/utils/notification_utils';
import { getCustomerActivitiesAction, getCustomerActivitiesState } from '../../../../../../features/customer/get_customer_activities';
import { MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_FORMAT } from '../../../../../../core/constants';
import PaginationControls from '../../../../../common/pagination_controls';
import { getDefaultPaginationByDateDescQueryValues } from '../../../../../../core/utils/api_utils';
import { getAllPodsAction, getAllPodsState } from '../../../../../../features/pod/get_all_pods';
import { CustomerActivityActionEnum } from '../../../../../../core/enums/customer/customer_activity';
import { getCartEventsAction, getCartEventsState, invalidateCartEvents } from '../../../../../../features/order/get_cart_events';
import { getCustomerActivityAction } from '../../../../../common/utils/customer_utils';
import { getCartEventAction } from '../../../../../common/utils/order_utils';

const initialQueryParams = {
    ...getDefaultPaginationByDateDescQueryValues
};

const ActivitiesList = ({ customerId }) => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();

    const [queryParams, setQueryParams] = useState(initialQueryParams);
    const [expandedRow, setExpandedRow] = useState(undefined);

    const getAllPods = useSelector(getAllPodsState);
    const getCustomerActivities = useSelector(getCustomerActivitiesState);
    const getCartEvents = useSelector(getCartEventsState);

    useEffect(() => {
        dispatch(getAllPodsAction())
            .then(response => showErrorMessage(getPodsNotificationData(response)));
        dispatch(getCustomerActivitiesAction({ queryParams, id: customerId }))
            .then(response => showErrorMessage(getCustomerActivitiesNotificationData(response)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, dispatch, queryParams]);

    const handleRowExpand = (activityId, date, nextLoginDate, action) => {
        if (expandedRow === activityId) {
            setExpandedRow(undefined);
            return;
        }

        dispatch(invalidateCartEvents());

        if (action === CustomerActivityActionEnum.LOGIN.value) {
            setExpandedRow(activityId);
            dispatch(getCartEventsAction({ customerId, fromDate: date, toDate: nextLoginDate }))
                .then(response => showErrorMessage(getCartEventsNotificationData(response)));
        }
    };

    return (
        <Box>
            { (getCustomerActivities?.loading || getAllPods?.loading) && (
                <Box mb={ 2 }>
                    <LinearProgress/>
                </Box>
            ) }

            { (getCustomerActivities?.data?.content && getAllPods?.data) && (
                <Box>
                    <TableContainer component={ Paper } variant="outlined">
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                    <TableCell sx={ { width: 50 } }/>
                                    <TableCell>Punkt sprzedaży</TableCell>
                                    <TableCell align="center">Data</TableCell>
                                    <TableCell align="right" sx={ { pr: 4 } }>Akcja</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                { getCustomerActivities?.data?.content?.map((activity) => {
                                    const isExpanded = expandedRow === activity?.id;
                                    const isLogin = activity?.action === CustomerActivityActionEnum.LOGIN.value;
                                    const podLabel = getAllPods?.data?.map(pod => ({
                                        id: pod?.id,
                                        label: `${ pod?.indoorPartner?.name } (${ pod?.description }) - ${ pod?.device?.serviceId || 'brak urządzenia' }`
                                    }))
                                        .find(pod => pod?.id === activity?.podId)?.label || 'Brak';

                                    return (
                                        <React.Fragment key={ activity?.id }>
                                            <TableRow hover sx={ { '& > *': { borderBottom: 'unset' } } }>
                                                <TableCell>
                                                    { isLogin && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={ () => handleRowExpand(activity?.id, activity?.date, activity?.nextLoginDate, activity?.action) }
                                                        >
                                                            { isExpanded ? <KeyboardArrowUpIcon/> :
                                                                <KeyboardArrowDownIcon/> }
                                                        </IconButton>
                                                    ) }
                                                </TableCell>
                                                <TableCell>
                                                    { podLabel }
                                                </TableCell>
                                                <TableCell align="center">
                                                    { moment(activity?.date, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                                </TableCell>
                                                <TableCell align="right" sx={ { pr: 4 } }>
                                                    <Chip
                                                        label={ getCustomerActivityAction(activity?.action) }
                                                        color={ isLogin ? "primary" : "default" }
                                                        variant={ isLogin ? "filled" : "outlined" }
                                                        size="small"
                                                        sx={ { fontWeight: 600 } }
                                                    />
                                                </TableCell>
                                            </TableRow>


                                            <TableRow>
                                                <TableCell style={ { paddingBottom: 0, paddingTop: 0 } } colSpan={ 4 }>
                                                    <Collapse in={ isExpanded } timeout="auto" unmountOnExit>
                                                        <Box sx={ {
                                                            margin: 2,
                                                            border: theme => `1px solid ${ theme.palette.divider }`,
                                                            borderRadius: 1,
                                                            overflow: 'hidden'
                                                        } }>
                                                            { getCartEvents?.loading ? (
                                                                <LinearProgress/>
                                                            ) : (
                                                                <Table size="small">
                                                                    <TableHead>
                                                                        <TableRow sx={ {
                                                                            '& th': {
                                                                                backgroundColor: 'slate.50',
                                                                                fontWeight: 600,
                                                                                fontSize: '0.8rem'
                                                                            }
                                                                        } }>
                                                                            <TableCell>ID Koszyka</TableCell>
                                                                            <TableCell align="center">Data</TableCell>
                                                                            <TableCell align="center">Akcja</TableCell>
                                                                            <TableCell align="left">Dodatkowe
                                                                                informacje</TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        { getCartEvents?.data?.map((cartEvent) => (
                                                                            <TableRow key={ cartEvent?.id } hover>
                                                                                <TableCell
                                                                                    sx={ { fontFamily: 'monospace' } }>{ cartEvent?.cartId }</TableCell>
                                                                                <TableCell align="center">
                                                                                    { moment(cartEvent?.date, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                                                                </TableCell>
                                                                                <TableCell align="center">
                                                                                    { getCartEventAction(cartEvent?.action) }
                                                                                </TableCell>
                                                                                <TableCell align="left">
                                                                                    { cartEvent?.additionalParams || '-' }
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        )) }
                                                                        { (!getCartEvents?.data || getCartEvents?.data?.length === 0) && (
                                                                            <TableRow>
                                                                                <TableCell colSpan={ 4 } align="center"
                                                                                           sx={ {
                                                                                               py: 3,
                                                                                               color: 'text.secondary'
                                                                                           } }>
                                                                                    Brak zarejestrowanych zdarzeń w
                                                                                    koszyku w tej sesji.
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        ) }
                                                                    </TableBody>
                                                                </Table>
                                                            ) }
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    );
                                }) }
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box mt={ 3 } display="flex" justifyContent="center">
                        <PaginationControls
                            currentPage={ getCustomerActivities?.data?.pageable?.pageNumber }
                            totalPages={ getCustomerActivities?.data?.totalPages }
                            setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                        />
                    </Box>
                </Box>
            ) }
        </Box>
    );
};

export default ActivitiesList;